import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { environment } from 'environments/environment';
import { ApiResponse } from '../../interfaces/httpResponses';
import { City, Province } from '../../interfaces/Locations';
import { skipLoaderHeaders } from 'app/core/interceptors/loader.interceptor';
import { BusyTracker } from 'app/shared/utils/busy-indicator';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private apiUrl = `${environment.apiUrl}locations`;

  private _isLoading$ = new Subject<boolean>();
  public isLoading$ = this._isLoading$.asObservable();

  private _provinces$ = new Subject<Province[]>();
  private provinces$ = this._provinces$.asObservable();

  private _cities$ = new Subject<City[]>();
  private cities$ = this._cities$.asObservable();

  private readonly provincesTracker = new BusyTracker();
  private readonly citiesTracker = new BusyTracker();
  /** Spinner streams for the province / city selectors (not the global overlay). */
  public readonly provincesBusy$ = this.provincesTracker.indicator$();
  public readonly citiesBusy$ = this.citiesTracker.indicator$();

  constructor(private httpClient: HttpClient) {}

  public getProvinces(): Observable<Province[]> {
    return this.provinces$;
  }

  public getCities(): Observable<City[]> {
    return this.cities$;
  }

  public loadProvinces(): Subscription {
    this._isLoading$.next(true);

    return this.provincesTracker
      .track(
        this.httpClient.get<ApiResponse<Province[]>>(
          `${this.apiUrl}/provinces`,
          { headers: skipLoaderHeaders() },
        ),
      )
      .subscribe({
        next: (response) => {
          this._provinces$.next(response.payload);
        },
        complete: () => this._isLoading$.next(false),
        error: () => this._isLoading$.next(false),
      });
  }

  public loadCities(city: string = '', province: string = ''): Subscription {
    this._isLoading$.next(true);

    const httpParams = new HttpParams()
      .set('city', city)
      .set('province', province);

    return this.citiesTracker
      .track(
        this.httpClient.get<ApiResponse<City[]>>(`${this.apiUrl}/cities`, {
          params: httpParams,
          headers: skipLoaderHeaders(),
        }),
      )
      .subscribe({
        next: (response) => {
          this._cities$.next(response.payload);
        },
        complete: () => this._isLoading$.next(false),
        error: () => this._isLoading$.next(false),
      });
  }
}
